import React, { useState } from "react";
import { Sparkles, Loader2, Table2, Lock, BookMarked } from "lucide-react";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import api from "../lib/apiClient";

const THREAT = {
  high: "bg-rose-500/15 text-rose-300 border-rose-500/30",
  medium: "bg-amber-500/15 text-amber-300 border-amber-500/30",
  low: "bg-emerald-500/15 text-emerald-300 border-emerald-500/30",
};

const COLS = [
  { key: "name", label: "Competitor" },
  { key: "strengths", label: "Core Strengths" },
  { key: "pricing", label: "Pricing Model" },
  { key: "market_share", label: "Market Share" },
  { key: "differentiation", label: "Your Edge" },
  { key: "source", label: "Source" },
];

export default function CompetitorTracker({ plan, value, onChange, readOnly }) {
  const [loading, setLoading] = useState(false);

  const aiFill = async () => {
    setLoading(true);
    try {
      const res = await api.post("/ai/suggest", {
        type: "competitors", business_name: plan.business_name, industry: plan.industry, description: plan.description,
      });
      onChange(res.data.items); // replace — this data is AI-managed, not user-editable
      toast.success("Competitor research refreshed");
    } catch (e) {
      toast.error(e.response?.data?.detail || "AI research failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="rounded-2xl border border-slate-800 bg-slate-900/50 p-5 sm:p-6">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-2">
        <div className="flex items-center gap-2">
          <Table2 className="h-5 w-5 text-sky-400" />
          <h3 className="font-display text-lg font-bold">Competitor Tracker</h3>
        </div>
        {!readOnly && (
          <Button data-testid="competitor-ai-button" size="sm" onClick={aiFill} disabled={loading} className="cta-btn">
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Sparkles className="h-4 w-4 mr-1" /> {value.length ? "Refresh research" : "Research competitors"}</>}
          </Button>
        )}
      </div>
      <p className="flex items-center gap-1.5 text-xs text-slate-500 mb-5">
        <Lock className="h-3 w-3" /> Auto-generated from AI market research — read only.
      </p>

      {value.length === 0 ? (
        <p className="text-sm text-slate-500 italic py-8 text-center">No competitor data yet. Click "Research competitors" to auto-fill this matrix.</p>
      ) : (
        <div className="overflow-x-auto no-scrollbar">
          <table className="w-full text-sm min-w-[920px]">
            <thead>
              <tr className="text-left text-xs uppercase text-slate-500 border-b border-slate-800">
                {COLS.map((c) => <th key={c.key} className="py-2 px-3 font-semibold">{c.label}</th>)}
                <th className="py-2 px-3 font-semibold">Threat</th>
              </tr>
            </thead>
            <tbody>
              {value.map((c) => (
                <tr key={c.id} data-testid={`competitor-row-${c.id}`} className="border-b border-slate-800/60 hover:bg-slate-800/30 transition-colors">
                  {COLS.map((col) => (
                    <td key={col.key} className="py-3 px-3 align-top text-slate-200">
                      {col.key === "name" ? (
                        <span className="font-semibold text-white">{c[col.key] || "—"}</span>
                      ) : col.key === "source" ? (
                        <span className="text-xs text-slate-500 italic flex items-start gap-1"><BookMarked className="h-3 w-3 mt-0.5 shrink-0" />{c[col.key] || "—"}</span>
                      ) : (c[col.key] || "—")}
                    </td>
                  ))}
                  <td className="py-3 px-3 align-top">
                    <Badge className={THREAT[c.threat_level] || THREAT.medium}>{c.threat_level}</Badge>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
